import { memoFn } from "@phosphor/utils";
import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { devLogger } from "#scripts/lib/dev/devLogger.ts";

export interface NameMetadata {
  collapsed: boolean;
  favorite: boolean;
}

export type NameStatesAtom = ReturnType<typeof getDevNameStatesAtom>;

const log = devLogger.named("devname-states");

/**
 * Return an atom storing name states for a given DevState name.
 * Uses localStorage to persist the record of key -> NameState.
 * We wrap this record in a Map for convenient usage.
 */
export const getDevNameStatesAtom = memoFn((devStateName: string) => {
  const baseAtom = atomWithStorage<Record<string, NameMetadata | undefined>>("dev-name-states:" + devStateName, {});

  // We expose a derived atom that reads/writes a Map<string, NameState | undefined>
  const devNameStatesAtom = atom(
    (get) => {
      const storeObj = get(baseAtom);
      const map = new Map<string, NameMetadata | undefined>();
      for (const key of Object.keys(storeObj)) {
        map.set(key, storeObj[key]);
      }
      return map;
    },
    (
      get,
      set,
      [nameKey, forceVal]: [
        name: string,
        force?:
          | Partial<NameMetadata>
          | ((current: undefined | NameMetadata) => Partial<NameMetadata> | null)
          | null
          | undefined,
      ],
    ) => {
      const storeObj = { ...get(baseAtom) };
      const current = storeObj[nameKey];
      const update = typeof forceVal === "function" ? forceVal(current) : forceVal;
      log._debug("Updating DevNameState appearance", { nameKey, update });
      if (update === null) {
        delete storeObj[nameKey];
      } else {
        storeObj[nameKey] = {
          collapsed: nameKey.startsWith("_"),
          favorite: false,
          ...current,
          ...update,
        };
      }
      set(baseAtom, storeObj);
    },
  );

  return devNameStatesAtom;
});
